import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, Baby, FileUp } from "lucide-react";

const emptyForm = {
  name: "",
  birth_date: "",
  nik: "",
  phone: "",
  desa: "",
  rt: "",
  rw: "",
  kecamatan: "Dawe",
  kabupaten: "Kudus",
};

export default function Usg() {
  const [form, setForm] = useState(emptyForm);
  const [ktp, setKtp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Nama lengkap dan No. WA wajib diisi");
      return;
    }
    if (!ktp) {
      toast.error("Foto KTP wajib diunggah");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("ktp", ktp);
      const { data } = await api.post("/public/usg", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pendaftaran Pemeriksaan Ibu Hamil dan USG Gratis berhasil dikirim");
      const sent = (data.wa_notifs || []).filter((w) => w.ok).length;
      if (sent > 0) toast.success("Notifikasi WA terkirim ke nomor Anda");
      setSuccess(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="usg-page">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <Link to="/"><img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" /></Link>
          <div className="flex-1">
            <p className="font-heading font-bold text-slate-900 leading-tight">Pemeriksaan Ibu Hamil & USG Gratis</p>
            <p className="text-xs text-slate-500">Klinik Pratama NU Medika Dawe × NU Care-LAZISNU Dawe</p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="usg-back-button">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Kembali
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 space-y-6">
        <img src="/banner-usg.jpeg" alt="Banner Program Pemeriksaan Ibu Hamil dan USG Gratis" className="w-full rounded-2xl shadow-sm border border-slate-200" data-testid="usg-banner" />

        {success ? (
          <Card data-testid="usg-success">
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <CheckCircle2 className="h-14 w-14 text-emerald-700" />
              <p className="font-heading text-xl font-bold text-slate-800">Pendaftaran Terkirim</p>
              <p className="text-sm text-slate-500 max-w-md">
                Data pendaftaran Pemeriksaan Ibu Hamil dan USG Gratis Anda sudah kami terima. Selanjutnya akan dilakukan asesmen oleh petugas kami. Jadwal pemeriksaan akan dikirim ke No. WA Anda setelah disetujui. Kuota harian 20 pasien. Terima kasih.
              </p>
              <Link to="/">
                <Button className="mt-2 bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="usg-success-back">Kembali ke Beranda</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="usg-form-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Baby className="h-5 w-5 text-emerald-800" />
                Form Pendaftaran Pemeriksaan Ibu Hamil & USG Gratis
              </CardTitle>
              <p className="text-xs text-slate-500">Isi data di bawah ini tanpa perlu login — No. Rekam Medis (RM) akan diisi oleh petugas setelah asesmen. Kuota harian 20 pasien.</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="usg-form">
                <div className="space-y-2">
                  <Label>Nama Lengkap *</Label>
                  <Input data-testid="usg-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Tanggal Lahir</Label>
                  <Input type="date" data-testid="usg-birth-date-input" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>No. KTP / NIK</Label>
                  <Input data-testid="usg-nik-input" value={form.nik} onChange={(e) => setForm({ ...form, nik: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Jenis Kelamin</Label>
                  <Input data-testid="usg-gender-input" value="Perempuan" disabled className="bg-slate-50" />
                </div>
                <div className="space-y-2">
                  <Label>No. WA *</Label>
                  <Input data-testid="usg-phone-input" placeholder="08xxxxxxxxxx" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Desa</Label>
                  <Input data-testid="usg-desa-input" value={form.desa} onChange={(e) => setForm({ ...form, desa: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>RT</Label>
                  <Input data-testid="usg-rt-input" value={form.rt} onChange={(e) => setForm({ ...form, rt: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>RW</Label>
                  <Input data-testid="usg-rw-input" value={form.rw} onChange={(e) => setForm({ ...form, rw: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Kecamatan</Label>
                  <Input data-testid="usg-kecamatan-input" value={form.kecamatan} onChange={(e) => setForm({ ...form, kecamatan: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Kabupaten</Label>
                  <Input data-testid="usg-kabupaten-input" value={form.kabupaten} onChange={(e) => setForm({ ...form, kabupaten: e.target.value })} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Unggah Foto KTP *</Label>
                  <label className="flex items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2.5 text-sm cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors">
                    <FileUp className="h-4 w-4 text-emerald-700 shrink-0" />
                    <span className="truncate text-slate-600" data-testid="usg-ktp-name">{ktp ? ktp.name : "Pilih file (PDF/JPG/PNG)"}</span>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="usg-ktp-input" onChange={(e) => setKtp(e.target.files?.[0] || null)} />
                  </label>
                </div>
                <div className="md:col-span-2">
                  <Button type="submit" data-testid="usg-submit-button" disabled={saving} className="w-full bg-emerald-800 hover:bg-emerald-700 text-white">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kirim Pendaftaran"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
